import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Container, Image } from '../../../components/Layout';
import { TextFilterInput } from '../../../components/FormElements';
import { Button } from '../../../components/Buttons';
import { Select } from '../../../components/Select';
import type { SelectOption } from '../../../components/Select';
import { Breakpoints, Color, Grid, Transition, Type, Shadow, Border } from '../../../app/StyleGuide';
import videoService from '../../../services/VideoService';
import LoadingIndicator from '../../../components/LoadingIndicator';
import EmptyState from '../../../components/EmptyState';
import { isAudioExtension } from '../../../utils/fileUtils';
import { Media } from '../models/timeline';

export const MediaTypes = Object.freeze({
  Image: 'image',
  Video: 'video',
  Audio: 'audio',
} as const);

type SectionMediaPickerProps = {
  mediaItems: Media[];
  onSelectMedia: (media: Media) => void;
  onUploadClick: () => void;
  typeLimit?: string;
  isSubmitting?: boolean;
  embedded?: boolean;
};

const TypeSelectContainer = styled.div`
  width: 140px;
  min-width: 140px;
`;

const UploadSlot = styled.div`
  flex: 0 0 auto;
`;

const ControlsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${Grid._4};
  align-items: flex-end;
  margin-bottom: ${Grid._6};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    flex-wrap: nowrap;
  }
`;

const MediaSearchWrap = styled.div`
  width: 100%;

  input {
    box-sizing: border-box;
    height: 42px;
    padding-top: 8px;
    padding-bottom: 8px;
  }
`;

const EmbeddedPickerLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: min(560px, calc(95vh - 240px));

  & > *:first-of-type {
    flex-shrink: 0;
  }
`;

const MediaScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const MediaItems = styled.div`
  display: grid;
  grid-column-gap: ${Grid._5};
  grid-row-gap: ${Grid._6};
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
`;

const MediaImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 260px;
  width: 100%;
  background: ${Color.Gray._20};
  margin-bottom: ${Grid._3};
  color: ${Type.Color.medium};

  ${Image} {
    height: 100%;
    width: 100%;
  }
`;

const MediaDetails = styled.div`
  word-wrap: anywhere;
  text-align: center;
`;

const MediaItemContainer = styled.div<{ selected?: boolean }>`
  min-width: 260px;
  cursor: pointer;

  ${MediaImageContainer} {
    border-radius: ${Border.radius};
    outline: 3px solid ${Color.Gray._20};
    transition: ${Transition.normal};

    ${props =>
      props.selected &&
      `outline-color: ${Color.Primary._50};
      box-shadow: ${Shadow.medium};`}
  }
`;

type MediaImageProps = {
  media: Media;
};

const MediaImage = ({ media }: MediaImageProps) => {
  const [status, setStatus] = useState({ isLoading: true, isError: false });
  const [src, setSrc] = useState<string | undefined>();

  const onImageLoaded = () => setStatus({ isLoading: false, isError: false });

  useEffect(() => {
    if (media._type === 'video') {
      videoService
        .getPoster(media.id)
        .then(posterUrl => {
          if (!posterUrl) return Promise.reject('Invalid poster response');

          setSrc(posterUrl);
        })
        .catch(error => {
          console.error(error);
          setStatus({ isLoading: false, isError: true });
        });
    } else {
      setSrc(media.url?.thumbnail);
    }
  }, [media._type, media.id, media.url?.thumbnail]);

  return (
    <MediaImageContainer>
      {status.isError ? (
        <span>{media.name}</span>
      ) : (
        <>
          {status.isLoading && <LoadingIndicator />}
          {src && (
            <Image
              src={src}
              alt={media.name}
              onLoad={onImageLoaded}
              style={{ display: status.isLoading ? 'none' : 'initial' }}
            />
          )}
        </>
      )}
    </MediaImageContainer>
  );
};

const isAudioMedia = (media: Media) => media._type === MediaTypes.Video && isAudioExtension(media.ext);

const AudioMediaImage = () => (
  <MediaImageContainer>
    <i className="fas fa-music" style={{ fontSize: Type.Scale._7 }}></i>
  </MediaImageContainer>
);

type MediaItemProps = {
  media: Media;
  onClick: () => void;
  selected?: boolean;
};

const MediaItem = ({ media, onClick, selected }: MediaItemProps) => (
  <MediaItemContainer data-qa-hook="mediaItem" onClick={onClick} selected={selected}>
    {isAudioMedia(media) ? <AudioMediaImage /> : <MediaImage media={media} />}
    <MediaDetails data-qa-hook="mediaName">{media.name}</MediaDetails>
  </MediaItemContainer>
);

const SectionMediaPicker = ({
  mediaItems,
  onSelectMedia,
  onUploadClick,
  typeLimit = '',
  isSubmitting = false,
  embedded = false,
}: SectionMediaPickerProps) => {
  const [selected, setSelected] = useState<Media | undefined>();
  const [textFilter, setTextFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState(typeLimit);

  const matchesTextFilter = (mediaName: string, filter: string) => {
    if (!filter) return true;

    return mediaName.trim().toLowerCase().includes(filter.toLowerCase());
  };

  const matchesTypeFilter = (media: Media, filter: string) => {
    switch (filter) {
      case MediaTypes.Image:
        return media._type === MediaTypes.Image;
      case MediaTypes.Video:
        return media._type === MediaTypes.Video && !isAudioMedia(media);
      case MediaTypes.Audio:
        return media._type === MediaTypes.Video && isAudioMedia(media);
      default:
        return true;
    }
  };

  const typeSelectOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: 'All Media' },
      ...Object.entries(MediaTypes).map(([displayName, type]) => ({
        value: type,
        label: displayName,
      })),
    ],
    []
  );

  const filteredOutMediaIds = useMemo(() => {
    if (!textFilter && !typeFilter) return [];

    return mediaItems
      .filter(
        media =>
          (!!textFilter && !matchesTextFilter(media.name, textFilter)) ||
          (!!typeFilter && !matchesTypeFilter(media, typeFilter))
      )
      .map(media => media.id);
  }, [mediaItems, textFilter, typeFilter]);

  const visibleMediaItems = useMemo(
    () => mediaItems.filter(media => !filteredOutMediaIds.includes(media.id)),
    [mediaItems, filteredOutMediaIds]
  );

  const handleMediaItemClick = (media: Media) => {
    if (isSubmitting) return;
    setSelected(media);
    onSelectMedia(media);
  };

  const controls = (
    <ControlsContainer>
      <MediaSearchWrap>
        <TextFilterInput
          placeholder="Search Media"
          value={textFilter}
          onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) => setTextFilter(e.target.value)}
          clearInput={() => setTextFilter('')}
        />
      </MediaSearchWrap>

      <TypeSelectContainer>
        <Select
          options={typeSelectOptions}
          value={typeFilter}
          onChange={value => setTypeFilter(String(value))}
          placeholder="All Media"
          disabled={!!typeLimit}
        />
      </TypeSelectContainer>

      <UploadSlot>
        <Button data-qa-hook="uploadMedia" onClick={onUploadClick}>
          <i className="fas fa-upload"></i> Upload Media
        </Button>
      </UploadSlot>
    </ControlsContainer>
  );

  const mediaGrid =
    visibleMediaItems.length === 0 ? (
      <div data-qa-hook="mediaSearchEmptyState">
        <EmptyState
          title={mediaItems.length === 0 ? 'No media available' : 'No matching media'}
          description={
            mediaItems.length === 0
              ? 'Upload new media to see it here.'
              : typeLimit
              ? 'Try a different search term.'
              : 'Try a different search term or change the media type filter.'
          }
        />
      </div>
    ) : (
      <MediaItems data-qa-hook="mediaContainer">
        {visibleMediaItems.map(media => (
          <MediaItem
            key={media.id}
            media={media}
            onClick={() => handleMediaItemClick(media)}
            selected={selected === media}
          />
        ))}
      </MediaItems>
    );

  if (embedded) {
    return (
      <EmbeddedPickerLayout data-qa-hook="sectionMediaPicker">
        {controls}
        <MediaScrollArea>{mediaGrid}</MediaScrollArea>
      </EmbeddedPickerLayout>
    );
  }

  return (
    <Container className="mb-48" data-qa-hook="sectionMediaPicker">
      {controls}
      {mediaGrid}
    </Container>
  );
};

export default SectionMediaPicker;
